
import React from "react";
import { Calendar, ShieldAlert, Clock, Award } from "lucide-react";

const ANNUAL_ENTITLEMENT = 24;
const DAYS_PER_MONTH = 2;

const RECOVERY_CODES = new Set(["RECUP", "RECUPERATION", "REPOS"]);

const ICONS = {
  ANNUEL: Calendar,
  MALADIE: ShieldAlert,
  REPOS: Clock,
  RECUP: Clock,
  RECUPERATION: Clock,
  SPECIAL: Award,
  MARIAGE: Award,
  NOUVEAU_NE: Award,
  NAISSANCE: Award,
  DECES_PROCHE: Award,
  DECES_FAMILLE: Award,
  CIRCONCISION: Award,
};

const COLORS = {
  ANNUEL: "blue",
  MALADIE: "red",
  REPOS: "green",
  RECUP: "green",
  RECUPERATION: "green",
  SPECIAL: "yellow",
  MARIAGE: "yellow",
  NOUVEAU_NE: "yellow",
  NAISSANCE: "yellow",
  DECES_PROCHE: "yellow",
  DECES_FAMILLE: "yellow",
  CIRCONCISION: "yellow",
};

const firstDefined = (...values) =>
  values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== ""
  );

const numberValue = (...values) => {
  const value = firstDefined(...values);

  if (value === undefined) return null;

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const nonNegative = (value) =>
  Math.max(0, Number.isFinite(value) ? value : 0);

const formatDays = (value) =>
  nonNegative(value).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  });

const normalizeCode = (item) =>
  String(
    item?.code ||
      item?.leave_type_code ||
      item?.type_code ||
      ""
  )
    .trim()
    .toUpperCase();

const normalizeBalances = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (value && typeof value === "object") {
    return Object.entries(value).map(([code, item]) => ({
      code,
      ...(item && typeof item === "object"
        ? item
        : { remaining: item }),
    }));
  }

  return [];
};

const safeFrenchDate = (value) => {
  if (!value) return null;

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleDateString("fr-FR");
};

const getAnnualDetails = (item) => {
  const n1Raw = numberValue(
    item.n1_remaining,
    item.remaining_n1,
    item.previous_year_remaining,
    item.annual_previous_remaining,
    item.annual_balance_n1
  );

  const nRaw = numberValue(
    item.n_remaining,
    item.remaining_n,
    item.current_year_remaining,
    item.annual_remaining_n,
    item.annual_balance_n
  );

  const hasN1 = n1Raw !== null;
  const hasN = nRaw !== null;

  const n1 = nonNegative(n1Raw ?? 0);
  const n = nonNegative(nRaw ?? 0);

  // Les soldes détaillés sont prioritaires :
  // le total doit toujours être N-1 + N.
  if (hasN1 && hasN) {
    return {
      hasDetails: true,
      n1,
      n,
      remaining: n1 + n,
    };
  }

  // Compatibilité avec les anciennes réponses API.
  const total = numberValue(
    item.annual_total_remaining,
    item.total_annual_remaining,
    item.total_remaining,
    item.remaining,
    item.annual_balance
  );

  return {
    hasDetails: false,
    n1,
    n,
    remaining: nonNegative(total ?? 0),
  };
};

const LeaveBalanceCards = ({
  balancesByType,
  balances_by_type,
  monthsWorked,
  months_worked,
  hireDate,
  hire_date,
  annualBalanceN1,
  annualBalanceN,
  annualTotalRemaining,
  loading = false,
  error = null,
}) => {
  const source = firstDefined(
    balancesByType,
    balances_by_type
  );

  const balances = normalizeBalances(source).filter(
    (item) => normalizeCode(item) !== "SANS_SOLDE"
  );

  const resolvedMonths = firstDefined(
    monthsWorked,
    months_worked
  );

  const resolvedHireDate = firstDefined(
    hireDate,
    hire_date
  );

  if (loading) {
    return (
      <div className="empty-state-container" role="status">
        <p className="empty-state-text">
          Chargement des soldes de congés…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state-container" role="alert">
        <p className="empty-state-text">
          Impossible de charger les soldes de congés.
          Vérifiez le serveur API.
        </p>
      </div>
    );
  }

  if (balances.length === 0) {
    return (
      <div className="empty-state-container">
        <p className="empty-state-text">
          Aucun solde de congé disponible.
        </p>
      </div>
    );
  }

  return (
    <div
      className="stat-cards-grid"
      style={{
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
        gap: "16px",
        width: "100%",
        minWidth: 0,
      }}
    >
      {balances.map((item, index) => {
        const code = normalizeCode(item);
        const Icon = ICONS[code] || Calendar;
        const color = COLORS[code] || "blue";

        const isAnnual = code === "ANNUEL";
        const isRecovery = RECOVERY_CODES.has(code);

        const usedN1Raw = numberValue(
          item.n1_used,
          item.used_n1,
          item.previous_year_used
        );

        const usedNRaw = numberValue(
          item.n_used,
          item.used_n,
          item.current_year_used
        );

        const hasSplitUsed =
          usedN1Raw !== null || usedNRaw !== null;

        const usedN1 = nonNegative(usedN1Raw ?? 0);
        const usedN = nonNegative(usedNRaw ?? 0);

        const generalUsed = numberValue(
          item.used,
          item.used_days
        );

        const used =
          isAnnual && hasSplitUsed
            ? usedN1 + usedN
            : nonNegative(generalUsed ?? 0);

        const acquired = nonNegative(
          numberValue(
            item.acquired,
            item.acquired_days
          ) ?? 0
        );

        // Pour le congé annuel, les soldes détaillés transmis par le
        // dashboard sont prioritaires. Le total affiché est N-1 + N.
        const annualItem = isAnnual
          ? {
              ...item,
              n1_remaining: firstDefined(
                annualBalanceN1,
                item.n1_remaining,
                item.remaining_n1,
                item.previous_year_remaining,
                item.annual_previous_remaining,
                item.annual_balance_n1
              ),
              n_remaining: firstDefined(
                annualBalanceN,
                item.n_remaining,
                item.remaining_n,
                item.current_year_remaining,
                item.annual_remaining_n,
                item.annual_balance_n
              ),
              annual_total_remaining: firstDefined(
                annualTotalRemaining,
                item.annual_total_remaining,
                item.total_annual_remaining,
                item.total_remaining,
                item.annual_balance
              ),
            }
          : item;

        const annual = isAnnual
          ? getAnnualDetails(annualItem)
          : null;

        const remaining = isAnnual
          ? annual.remaining
          : nonNegative(
              numberValue(
                item.remaining,
                item.remaining_days
              ) ?? (isRecovery ? acquired - used : 0)
            );

        const formattedHireDate =
          safeFrenchDate(resolvedHireDate);

        return (
          <div
            key={
              item.leave_type_id ??
              item.id ??
              `${code}-${index}`
            }
            className={`stat-card stat-card-${color}`}
            style={{
              minWidth: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <div className="stat-card-header">
              <span className="stat-card-title">
                {item.name || item.label || code || "Congé"}
              </span>

              <div
                className={`stat-card-icon-container stat-card-icon-${color}`}
              >
                <Icon size={20} />
              </div>
            </div>

            <div className="stat-card-body">
              <h3
                className="stat-card-value"
                style={{ overflowWrap: "anywhere" }}
              >
                {formatDays(remaining)} jours restants
              </h3>

              <div
                className="stat-card-trend"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "4px",
                  marginTop: "8px",
                }}
              >
                <span className="trend-label">
                  Utilisés : <strong>{formatDays(used)}</strong> j
                </span>

                {isAnnual && (
                  <>
                    <span className="trend-label">
                      Droit annuel :{" "}
                      <strong>
                        {ANNUAL_ENTITLEMENT} jours maximum par an
                      </strong>
                    </span>

                    {annual.hasDetails && (
                      <span className="trend-label">
                        Restant N-1 : {formatDays(annual.n1)} j
                        {" · "}
                        Restant N : {formatDays(annual.n)} j
                      </span>
                    )}

                    {hasSplitUsed && (
                      <span className="trend-label">
                        Utilisés N-1 : {formatDays(usedN1)} j
                        {" · "}
                        Utilisés N : {formatDays(usedN)} j
                      </span>
                    )}

                    {resolvedMonths !== undefined &&
                      resolvedMonths !== null && (
                        <span className="trend-label">
                          Ancienneté calculée :{" "}
                          {formatDays(
                            nonNegative(
                              numberValue(resolvedMonths) ?? 0
                            )
                          )}{" "}
                          mois · acquisition : {DAYS_PER_MONTH} j
                          par mois complet
                        </span>
                      )}

                    {formattedHireDate && (
                      <span className="trend-label">
                        Embauche : {formattedHireDate}
                      </span>
                    )}
                  </>
                )}

                {isRecovery && (
                  <span className="trend-label">
                    Solde de récupération géré par les RH.
                  </span>
                )}

                {item.description && (
                  <span className="trend-label">
                    {item.description}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LeaveBalanceCards;
